import React, { useCallback, useMemo, useState } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import SocialDmConversationList from "./SocialDmConversationList";
import { cn } from "@/lib/utils";

const STORAGE_KEY = "social-dm-sections-collapsed";

const DEFAULT_COLLAPSED = {
  direct: false,
  groups: false,
};

function loadCollapsed() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_COLLAPSED };
    const parsed = JSON.parse(raw);
    return {
      direct: !!parsed?.direct,
      groups: !!parsed?.groups,
    };
  } catch {
    return { ...DEFAULT_COLLAPSED };
  }
}

function unreadTotal(list) {
  return (list || []).reduce(
    (sum, c) => sum + (Number(c.unread_count) || 0),
    0
  );
}

function Section({
  id,
  title,
  conversations,
  collapsed,
  onToggle,
  selectedId,
  onSelect,
  currentUserId,
  emptyMessage,
  listClassName,
  borderTop,
}) {
  const unread = unreadTotal(conversations);
  return (
    <div
      className={cn(
        "flex flex-col min-h-0 overflow-hidden",
        borderTop && "border-t border-[hsl(var(--sm-divider))]"
      )}
    >
      <button
        type="button"
        onClick={() => onToggle(id)}
        className="shrink-0 flex items-center gap-1.5 px-4 py-2.5 text-left hover:bg-[hsl(var(--sm-accent)/0.06)] transition-colors"
        aria-expanded={!collapsed}
      >
        {collapsed ? (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        ) : (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground shrink-0" />
        )}
        <span className="text-[11px] font-medium tracking-wide text-muted-foreground flex-1 truncate">
          {title}
        </span>
        {unread > 0 ? (
          <span className="flex items-center gap-1.5 shrink-0">
            <span className="sm-unread-dot h-1.5 w-1.5 rounded-full" />
            <span className="text-[10px] tabular-nums text-muted-foreground">
              {unread > 99 ? "99+" : unread}
            </span>
          </span>
        ) : null}
      </button>
      {!collapsed && (
        <div className="min-h-0 overflow-y-auto overscroll-contain scrollbar-hide">
          <SocialDmConversationList
            conversations={conversations}
            loading={false}
            selectedId={selectedId}
            onSelect={onSelect}
            currentUserId={currentUserId}
            emptyMessage={emptyMessage}
            className={listClassName}
          />
        </div>
      )}
    </div>
  );
}

export default function SocialDmConversationSections({
  conversations,
  loading,
  selectedId,
  onSelect,
  currentUserId,
  listClassName,
  className,
}) {
  const [collapsed, setCollapsed] = useState(loadCollapsed);

  const toggle = useCallback((id) => {
    setCollapsed((prev) => {
      const next = { ...prev, [id]: !prev[id] };
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
      } catch {
        /* ignore */
      }
      return next;
    });
  }, []);

  const { direct, groups } = useMemo(() => {
    const list = conversations || [];
    return {
      direct: list.filter((c) => c.type !== "group"),
      groups: list.filter((c) => c.type === "group"),
    };
  }, [conversations]);

  const gridTemplateRows = useMemo(() => {
    const keys = ["direct", "groups"];
    return keys
      .map((k) => (collapsed[k] ? "auto" : "minmax(0, 1fr)"))
      .join(" ");
  }, [collapsed]);

  if (loading) {
    return (
      <div className={cn("p-4 text-sm text-muted-foreground", className)}>
        Loading…
      </div>
    );
  }

  const sectionProps = {
    onToggle: toggle,
    selectedId,
    onSelect,
    currentUserId,
    listClassName,
  };

  return (
    <div
      className={cn("grid min-h-0 h-full", className)}
      style={{ gridTemplateRows }}
    >
      <Section
        id="direct"
        title="Direct"
        conversations={direct}
        collapsed={collapsed.direct}
        emptyMessage="No direct messages yet."
        {...sectionProps}
      />
      <Section
        id="groups"
        title="Groups"
        conversations={groups}
        collapsed={collapsed.groups}
        emptyMessage="No groups yet."
        borderTop
        {...sectionProps}
      />
    </div>
  );
}
